import { RestSchemas } from '@/contexts/QueryProvider'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/shadcn/sidebar'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useGetAppMenu } from './useGetAppMenu'
import { Skeleton } from '@/components/shadcn/skeleton'
import { IconDocumentOutline } from '@icons/IconDocumentOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { CATALOG_BASE_PATH } from '@/const'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { AnimatePresence, motion } from 'motion/react'

type MessagesLandingMenuItem = RestSchemas['Messages.Landing.MenuItem'] & { iconName?: string }

function mergeOpenItems(current: string[], nextValues: string[]) {
  return Array.from(new Set([...current, ...nextValues]))
}

function findPathToPage(menu: MessagesLandingMenuItem[], pathname: string): MessagesLandingMenuItem[] | null {
  for (const menuItem of menu) {
    if (`${CATALOG_BASE_PATH}${menuItem.page_id}` === pathname) {
      return [menuItem]
    }

    if (menuItem.items?.length) {
      const childPath = findPathToPage(menuItem.items, pathname)

      if (childPath) {
        return [menuItem, ...childPath]
      }
    }
  }

  return null
}

function getActiveBranchIds(menu: MessagesLandingMenuItem[], pathname: string) {
  const activePath = findPathToPage(menu, pathname)

  if (!activePath) {
    return []
  }

  return activePath.filter((menuItem) => menuItem.items?.length).map((menuItem) => menuItem.page_id)
}

function getDescendantBranchIds(menuItem: MessagesLandingMenuItem): string[] {
  const branchIds: string[] = []

  for (const childItem of menuItem.items ?? []) {
    if (!childItem.items?.length) {
      continue
    }

    branchIds.push(childItem.page_id, ...getDescendantBranchIds(childItem))
  }

  return branchIds
}

export function AppMenu({
  sidebarOpen,
  setSidebarOpen,
}: {
  sidebarOpen: boolean
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { data } = useGetAppMenu()
  const { items } = data
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const activeBranchIds = useMemo(() => getActiveBranchIds(items, pathname), [items, pathname])
  const [openItems, setOpenItems] = useState<string[]>(() => activeBranchIds)

  const handleLeafClick = useCallback(
    (pageId: string) => {
      const queryString = searchParams.toString()
      const destination = `${CATALOG_BASE_PATH}${pageId}${queryString ? `?${queryString}` : ''}`
      navigate(destination)
    },
    [navigate, searchParams]
  )

  const handleBranchOpenChange = useCallback(
    (menuItem: MessagesLandingMenuItem, nextOpen: boolean) => {
      if (nextOpen && !sidebarOpen) {
        setSidebarOpen(true)
      }

      setOpenItems((curr) => {
        if (nextOpen) {
          return mergeOpenItems(curr, [menuItem.page_id])
        }

        const branchIdsToRemove = new Set([menuItem.page_id, ...getDescendantBranchIds(menuItem)])
        return curr.filter((value) => !branchIdsToRemove.has(value))
      })
    },
    [setSidebarOpen, sidebarOpen]
  )

  useEffect(() => {
    if (!sidebarOpen) {
      setOpenItems([])
      return
    }

    setOpenItems((curr) => mergeOpenItems(curr, activeBranchIds))
  }, [activeBranchIds, sidebarOpen])

  return (
    <SidebarMenu className="gap-0 pr-2">
      <MenuItems
        menu={items}
        level={0}
        openItems={openItems}
        pathname={pathname}
        sidebarOpen={sidebarOpen}
        onBranchOpenChange={handleBranchOpenChange}
        onLeafClick={handleLeafClick}
      />
    </SidebarMenu>
  )
}

const MenuItems = ({
  menu,
  level,
  openItems,
  pathname,
  sidebarOpen,
  onBranchOpenChange,
  onLeafClick,
}: {
  menu: MessagesLandingMenuItem[]
  level: number
  openItems: string[]
  pathname: string
  sidebarOpen: boolean
  onBranchOpenChange: (menuItem: MessagesLandingMenuItem, nextOpen: boolean) => void
  onLeafClick: (pageId: string) => void
}) => {
  return (
    <>
      {menu.map((menuItem) => (
        <MenuItemNode
          key={menuItem.page_id ?? menuItem.name}
          level={level}
          menuItem={menuItem}
          openItems={openItems}
          pathname={pathname}
          sidebarOpen={sidebarOpen}
          onBranchOpenChange={onBranchOpenChange}
          onLeafClick={onLeafClick}
        />
      ))}
    </>
  )
}

function MenuItemNode({
  level,
  menuItem,
  openItems,
  pathname,
  sidebarOpen,
  onBranchOpenChange,
  onLeafClick,
}: {
  level: number
  menuItem: MessagesLandingMenuItem
  openItems: string[]
  pathname: string
  sidebarOpen: boolean
  onBranchOpenChange: (menuItem: MessagesLandingMenuItem, nextOpen: boolean) => void
  onLeafClick: (pageId: string) => void
}) {
  const isRoot = level === 0
  const isLeaf = !menuItem.items?.length
  const hasChildren = menuItem.items?.length > 0
  const isOpen = openItems.includes(menuItem.page_id)
  const isActive = pathname === `${CATALOG_BASE_PATH}${menuItem.page_id}`

  const itemClassName = clsx({
    'mt-2': !isLeaf || (isLeaf && isRoot),
  })

  const buttonClassName = clsx('relative min-w-0 h-9', {
    '!pl-9': level > 0 && sidebarOpen,
  })

  const labelClassName = clsx('min-w-0 flex-1 truncate font-light transition-opacity duration-200', {
    invisible: isRoot && !sidebarOpen,
    // 'font-normal': !isLeaf && isRoot,
    'font-normal': hasChildren,
    '!font-bold tracking-tighter text-foreground': isLeaf && isActive,
  })

  const menuItemContent = (
    <div
      style={{ paddingLeft: level * 8 }}
      className={clsx(
        'flex min-w-0 flex-1 items-center gap-x-2 overflow-hidden transition-[max-width] duration-300 ease-in-out',
        {
          'max-w-[252px]': sidebarOpen,
          'max-w-[36px]': !sidebarOpen,
        }
      )}
    >
      <AnimatePresence>
        {isActive && (
          <motion.div
            layoutId="menu-item-indicator"
            className={clsx('absolute left-0 top-0 h-full rounded bg-foreground/5', {
              'w-[calc(100%-8px)]': sidebarOpen,
              'w-[calc(100%-16px)]': !sidebarOpen,
            })}
          >
            <div className="absolute left-0 top-[calc(50%-12px)] h-[24px] w-[4.5px] rounded-full bg-foreground" />
          </motion.div>
        )}
      </AnimatePresence>
      {isRoot && (
        <DynamicIcon
          size={20}
          iconName={menuItem.icon_name ?? ''}
          lazyLoad={false}
          fallback={<IconDocumentOutline size={24} />}
          className="shrink-0"
        />
      )}
      <span className={labelClassName}>{menuItem.name}</span>
      {!isLeaf && sidebarOpen && (
        <span className="ml-auto shrink-0">
          <IconRightChevronOutline
            className={clsx('size-3 transition-transform duration-200', {
              'rotate-90': isOpen,
            })}
          />
        </span>
      )}
    </div>
  )

  if (isLeaf) {
    const ButtonComponent = isRoot ? SidebarMenuButton : SidebarMenuSubButton
    const MenuItemComponent = isRoot ? SidebarMenuItem : SidebarMenuSubItem

    const button = (
      <ButtonComponent
        isActive={isActive}
        aria-current={isActive ? 'page' : undefined}
        className={buttonClassName}
        onClick={() => onLeafClick(menuItem.page_id)}
      >
        {menuItemContent}
      </ButtonComponent>
    )

    return (
      <MenuItemComponent className={itemClassName}>
        <TooltipButton tooltip={menuItem.name} side="right" disabled={sidebarOpen}>
          {button}
        </TooltipButton>
      </MenuItemComponent>
    )
  }

  const ButtonComponent = isRoot ? SidebarMenuButton : SidebarMenuSubButton
  const MenuItemComponent = isRoot ? SidebarMenuItem : SidebarMenuSubItem

  const button = (
    <CollapsibleTrigger asChild>
      <ButtonComponent isActive={isActive} className={buttonClassName}>
        {menuItemContent}
      </ButtonComponent>
    </CollapsibleTrigger>
  )

  return (
    <Collapsible
      open={sidebarOpen && isOpen}
      onOpenChange={(nextOpen) => onBranchOpenChange(menuItem, nextOpen)}
      className="group/collapsible"
    >
      <MenuItemComponent className={itemClassName}>
        <TooltipButton tooltip={menuItem.name} side="right" disabled={sidebarOpen}>
          {button}
        </TooltipButton>
        <CollapsibleContent className="overflow-hidden text-sm transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub className="pt-1">
            <MenuItems
              menu={menuItem.items}
              level={level + 1}
              openItems={openItems}
              pathname={pathname}
              sidebarOpen={sidebarOpen}
              onBranchOpenChange={onBranchOpenChange}
              onLeafClick={onLeafClick}
            />
          </SidebarMenuSub>
        </CollapsibleContent>
      </MenuItemComponent>
    </Collapsible>
  )
}

export function MenuLoading() {
  const lines = Array.from({ length: 10 }, (_, i) => i)
  return (
    <div className="flex w-56 flex-col gap-y-2">
      {lines.map((line) => (
        <Skeleton key={line} className="h-8 w-11/12" />
      ))}
    </div>
  )
}
